<template>
  <div class="flex w-full justify-center">
    <div class="m-4 w-1/2 min-w-min max-w-4xl text-black">
      <Breadcrumb :breadcrumbs="breadcrumbs" class="mb-4"/>
      <div class="flex pb-4">
        <div class="grow">
          <span v-show="!current_id" class="text-2xl font-bold">{{ t( 'Create product' ) }}</span>
          <span v-show="current_id" class="text-2xl font-bold">{{ t( 'Update product' ) }}</span>
        </div>
        <div class="flex gap-2">
          <LoadingButton v-if="current_id && is_deleteable && is_delete_auth()" :label="t( 'Delete' )" ref="delete_btn" @click="click_to_delete()" colors="bg-red-600 text-white hover:bg-red-700"></LoadingButton>
          <LoadingButton v-if="is_create_or_update_auth()" :label="t( 'Save' )" ref="save_btn" @click="click_to_save()"></LoadingButton>
        </div>
      </div>
      <!-- Name -->
      <div class="mb-4 rounded-md shadow-md">
        <label class="mb-2 block bg-gray-100 p-2 font-bold" for="name">{{ t( 'Name' ) }}</label>
        <InputTextRequired ref="input_name" name="name" v-model="input.name" :show_required="input_name_required" @change="input_name_required = false" :key="key_rerender"></InputTextRequired>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useModal } from 'vue-final-modal';
import { useRoute } from 'vue-router';
import router from '@/router';
import store from '@/store';
import axios from '@/features/axios';
import utils from '@/features/utils';
import ConfirmModal from '@/components/modal/ConfirmModal.vue';
import API_RIGHTS from '@/constants/rights.constants';

export default {
  name: 'product-item-view',
  components: {
  },
  setup() {
    const { t } = useI18n();
    const route = useRoute();
    const current_id = utils.get_params_id( route.params.id );
    const breadcrumbs = ref ( [
      { label: t( 'Administration' ) },
      { label: t( 'Products' ), route: '/products' }
    ] );
    const input = ref( { name: '' } );
    const save_btn = ref( null );
    const delete_btn = ref( null );
    const input_name = ref( null );
    const input_name_required = ref( null );
    const key_rerender = ref ( 0 );
    const is_deleteable = ref( false ) ;

    const force_init_rerender = () => {
      key_rerender.value += 1;
    };

    const initialize = () => {
      if ( current_id ) {
        axios.get( 'api/product/' + current_id, { headers: { 'auth-token': store.getters.getToken } } )
          .then( async response => {
            if ( response.status == 200 ) {
              input.value.name = response.data.name;
              is_deleteable.value = response.data.is_deleteable;
              force_init_rerender();
            } else {
              utils.show_alert_from_resp( t, response );
              router.push( '/products' );
            }
          } )
          .catch( e => {
            analyse_catch( e );
          } );
      }
    };

    const click_to_save = () => {
      if ( typeof ( input.value.name ) != 'string' || input.value.name.trim() == '' ) {
        input_name_required.value = true;
        input_name.value.focus();
        save_btn.value.stop_loading();
        return;
      }

      input_name_required.value = false;

      if ( current_id ) {
        update_product();
      } else {
        create_new_product();
      }
    };

    const create_new_product = () => {
      let data = {
        name: input.value.name
      };

      axios.post( 'api/product/', data, { headers: { 'auth-token': store.getters.getToken } } )
        .then( response => {
          if ( response.status == 200 ) {
            router.push( '/products' );
          } else {
            utils.show_alert_from_resp( t, response );
          }
          save_btn.value.stop_loading();
        } )
        .catch( e => {
          save_btn.value.stop_loading();
          analyse_catch( e );
        } );
    };

    const update_product = () => {
      let data = {
        name: input.value.name,
      };

      axios.put( 'api/product/' + current_id, data, { headers: { 'auth-token': store.getters.getToken } } )
        .then( response => {
          if ( response.status == 201 ) {
            router.push( '/products' );
          } else {
            utils.show_alert_from_resp( t, response );
          }
          save_btn.value.stop_loading();
        } )
        .catch( e => {
          save_btn.value.stop_loading();
          analyse_catch( e );
        } );
    };

    const click_to_delete = () => {
      confirm_delete_modal.open();
    };

    const confirm_delete_modal = useModal( {
      component: ConfirmModal,
      attrs: {
        title: t( 'Delete product' ),
        message: t( 'Confirm delete product' ),
        onConfirm() {
          delete_product();
          confirm_delete_modal.close();
        },
        onCancel() {
          confirm_delete_modal.close();
          delete_btn.value.stop_loading();
        }
      }
    } );

    const delete_product = () => {
      axios.delete( 'api/product/' + current_id, { headers: { 'auth-token': store.getters.getToken } } )
        .then( response => {
          if ( response.status == 201 ) {
            router.push( '/products' );
          } else {
            utils.show_alert_from_resp( t, response );
          }
          delete_btn.value.stop_loading();
        } )
        .catch( e => {
          delete_btn.value.stop_loading();
          analyse_catch( e );
        } );
    };

    const is_create_auth = () => {
      return utils.is_auth( [ API_RIGHTS.CREATE_PRODUCTS ] );
    };

    const is_update_auth = () => {
      return utils.is_auth( [ API_RIGHTS.UPDATE_PRODUCTS ] );
    };

    const is_delete_auth = () => {
      return utils.is_auth( [ API_RIGHTS.DELETE_PRODUCTS ] );
    };

    const is_create_or_update_auth = () => {
      return current_id && is_update_auth() || !current_id && is_create_auth();
    };

    const analyse_catch = e => {
      utils.analyse_catch( e, router, '/products' );
    };

    initialize();

    return { t, current_id, key_rerender, is_deleteable,
      save_btn, delete_btn, input_name, input_name_required,
      breadcrumbs, input,
      is_create_auth, is_update_auth, is_delete_auth, is_create_or_update_auth,
      click_to_save, click_to_delete };
  }
};
</script>>